import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type QuoteLine = {
  id?: number
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
  product: number | null
  sort_order: number
  subtotal?: string
  tax_amount?: string
  line_total?: string
}

type Quote = {
  id: number
  number: string
  partner: number
  partner_name?: string
  quote_date: string
  valid_until: string | null
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED'
  currency_code: string
  notes: string
  subtotal: string
  tax_total: string
  total: string
  lines: QuoteLine[]
  converted_to_order: number | null
  converted_to_order_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }
type Account = { id: number; code: string; name: string; account_type: string; is_active: boolean }
type Product = { id: number; sku: string; name: string; sales_price: string; is_active: boolean }

const STATUS_COLOR: Record<Quote['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-amber-100 text-amber-800',
}

type TabKey = 'details' | 'lines' | 'files'

// ─────────────────── Page ───────────────────
export default function SalesQuoteDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const quoteId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const quoteQ = useQuery({
    queryKey: ['sales', 'quote', quoteId],
    queryFn: async () => (await api.get<Quote>(`/sales/quotes/${quoteId}/`)).data,
    enabled: !!quoteId,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/finance/accounts/')).data,
  })
  const productsQ = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: async () => (await api.get<Product[]>('/inventory/products/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Quote>) =>
      (await api.patch(`/sales/quotes/${quoteId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'quote', quoteId] })
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/sales/quotes/${quoteId}/`),
    onSuccess: () => {
      toast.success('Quote deleted')
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      navigate('/admin/sales/quotes')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const convertMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/sales/quotes/${quoteId}/convert_to_order/`)).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['sales', 'quote', quoteId] })
      qc.invalidateQueries({ queryKey: ['sales', 'quotes'] })
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      toast.success('Converted to order')
      // Drop the user on the new order's record page if it exists.
      if (data?.id) navigate(`/admin/sales/orders/${data.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (quoteQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (quoteQ.isError || !quoteQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Quote not found.</p>
          <Link to="/admin/sales/quotes" className="text-blue-600 hover:underline">← Back to quotes</Link>
        </div>
      </AdminLayout>
    )
  }

  const quote = quoteQ.data
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const revenueAccounts = (accountsQ.data || []).filter(
    (a) => a.is_active && a.account_type === 'REVENUE'
  )
  const products = (productsQ.data || []).filter((p) => p.is_active)
  const locked = quote.converted_to_order != null
  const canConvert = quote.status === 'ACCEPTED' && !locked

  const save = (payload: Partial<Quote>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/sales/quotes" className="text-muted-foreground hover:text-foreground hover:underline">← Quotes</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{quote.number}</div>
            <h2 className="text-2xl font-bold text-foreground">
              {quote.partner_name || <span className="italic">No customer</span>}
            </h2>
            <div className="text-sm text-muted-foreground">
              <span>Quoted {quote.quote_date}</span>
              {quote.valid_until && <span> · Valid until {quote.valid_until}</span>}
              <span> · </span>
              <span className="font-mono">{quote.currency_code} {Number(quote.total).toFixed(2)}</span>
              {quote.converted_to_order_number && (
                <span> · Converted to <Link to={`/admin/sales/orders/${quote.converted_to_order}`}
                  className="text-blue-600 hover:underline font-mono">{quote.converted_to_order_number}</Link></span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[quote.status]}`}>{quote.status}</span>
            ) : (
              <InlineStatus
                value={quote.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'SENT', label: 'Sent' },
                  { value: 'ACCEPTED', label: 'Accepted' },
                  { value: 'REJECTED', label: 'Rejected' },
                  { value: 'EXPIRED', label: 'Expired' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canConvert && (
                  <DropdownMenuItem
                    onClick={() => confirm(
                      `Convert quote ${quote.number} to a sales order`,
                      () => convertMut.mutate(),
                    )}
                    disabled={convertMut.isPending}
                    className="text-green-700"
                  >
                    Convert to order
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={locked}
                  onClick={() => !locked && confirm(
                    `quote ${quote.number}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!locked ? 'text-destructive' : ''}
                >
                  {locked ? `Delete (converted to ${quote.converted_to_order_number})` : 'Delete quote'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'lines', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'lines' && `Line items (${quote.lines.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          quote={quote}
          partners={partners}
          locked={locked}
          onSave={save}
        />
      )}
      {tab === 'lines' && (
        <LinesTab
          quote={quote}
          revenueAccounts={revenueAccounts}
          products={products}
          locked={locked}
          onSaveLines={(newLines) => save({ lines: newLines } as Partial<Quote>)}
        />
      )}
      {tab === 'files' && <FilesTab parent="sales.quote" parentId={quoteId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  quote, partners, locked, onSave,
}: {
  quote: Quote
  partners: Partner[]
  locked: boolean
  onSave: (payload: Partial<Quote>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This quote has been <strong>converted to order</strong>{' '}
          ({quote.converted_to_order_number}) and is read-only. Edit the order instead.
        </div>
      )}

      <Section title="Quote header">
        <Row label="Number">
          <InlineText
            value={quote.number}
            onSave={(v) => onSave({ number: v })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Customer">
          <InlineSelect
            value={String(quote.partner)}
            displayLabel={quote.partner_name || <Dash />}
            options={partners.map((p) => ({
              value: String(p.id),
              label: `${p.code} ${p.name}`,
              searchText: `${p.code} ${p.name}`,
            }))}
            onSave={(v) => onSave({ partner: Number(v) })}
            disabled={locked}
          />
        </Row>
        <Row label="Quote date">
          <InlineDate
            value={quote.quote_date}
            onSave={(v) => onSave({ quote_date: v || quote.quote_date })}
          />
        </Row>
        <Row label="Valid until">
          <InlineDate value={quote.valid_until} onSave={(v) => onSave({ valid_until: v })} />
        </Row>
        <Row label="Currency">
          <InlineText
            value={quote.currency_code}
            onSave={(v) => onSave({ currency_code: v.toUpperCase().slice(0, 3) })}
            disabled={locked}
            className="font-mono"
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[quote.status]}`}>{quote.status}</span>
          ) : (
            <InlineStatus
              value={quote.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'SENT', label: 'Sent' },
                { value: 'ACCEPTED', label: 'Accepted' },
                { value: 'REJECTED', label: 'Rejected' },
                { value: 'EXPIRED', label: 'Expired' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
      </Section>

      <Section title="Totals">
        <Row label="Subtotal">
          <span className="font-mono">{Number(quote.subtotal).toFixed(2)} {quote.currency_code}</span>
        </Row>
        <Row label="Tax">
          <span className="font-mono">{Number(quote.tax_total).toFixed(2)} {quote.currency_code}</span>
        </Row>
        <Row label="Total">
          <span className="font-mono font-bold text-base">
            {Number(quote.total).toFixed(2)} {quote.currency_code}
          </span>
        </Row>
        {quote.converted_to_order_number && (
          <Row label="Converted to order">
            <Link to={`/admin/sales/orders/${quote.converted_to_order}`}
              className="text-blue-600 hover:underline font-mono text-sm">
              {quote.converted_to_order_number}
            </Link>
          </Row>
        )}
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={quote.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            disabled={locked}
            placeholder="Customer-visible notes on this quote"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Line items tab ───────────────────

type LineDraft = {
  description: string
  quantity: string
  unit_price: string
  tax_rate: string
  revenue_account: number | null
  product: number | null
}

function emptyLineDraft(revenueAccounts: Account[]): LineDraft {
  return {
    description: '',
    quantity: '1',
    unit_price: '0',
    tax_rate: '0',
    revenue_account: revenueAccounts[0]?.id ?? null,
    product: null,
  }
}

function LinesTab({
  quote, revenueAccounts, products, locked, onSaveLines,
}: {
  quote: Quote
  revenueAccounts: Account[]
  products: Product[]
  locked: boolean
  onSaveLines: (lines: QuoteLine[]) => Promise<void>
}) {
  const { confirm, dialog } = useDeleteConfirm()
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<LineDraft>(emptyLineDraft(revenueAccounts))
  const [pending, setPending] = useState(false)

  function openCreate() {
    setDraft(emptyLineDraft(revenueAccounts))
    setCreating(true)
  }

  async function saveLineField(idx: number, patch: Partial<QuoteLine>) {
    const next = quote.lines.map((l, i) => i === idx ? { ...l, ...patch } : l)
    await onSaveLines(serializeLines(next))
  }

  async function addLine() {
    setPending(true)
    try {
      const next: QuoteLine[] = [
        ...quote.lines,
        {
          description: draft.description,
          quantity: draft.quantity,
          unit_price: draft.unit_price,
          tax_rate: draft.tax_rate,
          revenue_account: draft.revenue_account,
          product: draft.product,
          sort_order: quote.lines.length,
        },
      ]
      await onSaveLines(serializeLines(next))
      toast.success('Line added')
      setCreating(false)
    } catch (e) {
      toast.error(extractErr(e))
    } finally {
      setPending(false)
    }
  }

  async function removeLine(idx: number) {
    const next = quote.lines.filter((_, i) => i !== idx)
    try {
      await onSaveLines(serializeLines(next))
      toast.success('Line removed')
    } catch (e) {
      toast.error(extractErr(e))
    }
  }

  // Auto-fill description + unit_price when a product is picked in the draft.
  function setDraftProduct(productId: number | null) {
    const p = products.find((x) => x.id === productId)
    if (p) {
      setDraft({
        ...draft,
        product: productId,
        // Prefill description + price only when fields are still default.
        description: draft.description || p.name,
        unit_price: draft.unit_price === '0' ? p.sales_price : draft.unit_price,
      })
    } else {
      setDraft({ ...draft, product: productId })
    }
  }

  // Live footer totals.
  let subtotal = 0, taxTotal = 0
  quote.lines.forEach((l) => {
    const s = Number(l.quantity) * Number(l.unit_price)
    const t = s * Number(l.tax_rate) / 100
    subtotal += s; taxTotal += t
  })

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          {locked
            ? 'Quote converted — line items are read-only. Edit the resulting order instead.'
            : 'Edit quantities, unit prices, and tax rates inline. Totals recompute on save.'}
        </p>
        <Button size="sm" onClick={openCreate} disabled={locked}>
          <Plus className="h-3 w-3 mr-1" /> Add line
        </Button>
      </div>

      {quote.lines.length === 0 ? (
        <p className="text-muted-foreground text-sm">No line items yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Description</Th>
                <Th>Product</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit price</Th>
                <Th className="text-right">Tax %</Th>
                <Th>Revenue account</Th>
                <Th className="text-right">Line total</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {quote.lines.map((l, i) => {
                const s = Number(l.quantity) * Number(l.unit_price)
                const t = s * Number(l.tax_rate) / 100
                const acct = revenueAccounts.find((a) => a.id === l.revenue_account)
                const prod = l.product ? products.find((p) => p.id === l.product) : null
                return (
                  <tr key={l.id ?? i}>
                    <Td className="font-medium">
                      <InlineText
                        value={l.description}
                        onSave={(v) => saveLineField(i, { description: v })}
                        disabled={locked}
                        placeholder="Item description"
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.product ? String(l.product) : ''}
                        displayLabel={prod ? <span className="font-mono">{prod.sku}</span> : <Dash />}
                        allowClear
                        options={products.map((p) => ({
                          value: String(p.id),
                          label: `${p.sku} — ${p.name}`,
                          searchText: `${p.sku} ${p.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { product: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.quantity}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { quantity: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.unit_price}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { unit_price: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                      />
                    </Td>
                    <Td className="text-right">
                      <InlineNumber
                        value={l.tax_rate}
                        step="0.01"
                        onSave={(v) => saveLineField(i, { tax_rate: v })}
                        disabled={locked}
                        displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}%</span>}
                      />
                    </Td>
                    <Td className="text-xs">
                      <InlineSelect
                        value={l.revenue_account ? String(l.revenue_account) : ''}
                        displayLabel={acct ? `${acct.code} ${acct.name}` : <Dash />}
                        allowClear
                        options={revenueAccounts.map((a) => ({
                          value: String(a.id),
                          label: `${a.code} ${a.name}`,
                          searchText: `${a.code} ${a.name}`,
                        }))}
                        onSave={(v) => saveLineField(i, { revenue_account: v ? Number(v) : null })}
                        disabled={locked}
                      />
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {(s + t).toFixed(2)}
                    </Td>
                    <Td className="text-right">
                      {!locked && (
                        <RowActions
                          onEdit={() => {/* inline edits cover the whole row */}}
                          onDelete={() => confirm(
                            `line "${l.description || '(blank)'}"`,
                            () => removeLine(i),
                          )}
                        />
                      )}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-muted/40 text-sm">
              <tr>
                <td colSpan={6} className="px-4 py-2 text-right font-medium text-muted-foreground">Subtotal</td>
                <td className="px-4 py-2 text-right font-mono">{subtotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr>
                <td colSpan={6} className="px-4 py-2 text-right font-medium text-muted-foreground">Tax</td>
                <td className="px-4 py-2 text-right font-mono">{taxTotal.toFixed(2)}</td>
                <td />
              </tr>
              <tr className="font-bold">
                <td colSpan={6} className="px-4 py-2 text-right text-foreground">Total</td>
                <td className="px-4 py-2 text-right font-mono">{quote.currency_code} {(subtotal + taxTotal).toFixed(2)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="Add line item"
        description="Add a quotable line. Picking a product pre-fills description and unit price."
        onSubmit={addLine}
        isPending={pending}
        submitLabel="Add line"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="li-prod">Product</FieldLabel>
            <SearchableSelect
              id="li-prod"
              value={draft.product ? String(draft.product) : NONE}
              onChange={(v) => setDraftProduct(v === NONE ? null : Number(v))}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...products.map((p) => ({
                  value: String(p.id), label: p.name, hint: p.sku,
                })),
              ]}
            />
            <FieldDescription>Optional. Auto-fills description and price.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="li-desc">Description *</FieldLabel>
            <Textarea id="li-desc" autoFocus required value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={2} />
          </Field>
          <div className="grid grid-cols-3 gap-4">
            <Field>
              <FieldLabel htmlFor="li-qty">Quantity</FieldLabel>
              <Input id="li-qty" type="number" step="0.01" value={draft.quantity}
                onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-price">Unit price</FieldLabel>
              <Input id="li-price" type="number" step="0.01" value={draft.unit_price}
                onChange={(e) => setDraft({ ...draft, unit_price: e.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="li-tax">Tax %</FieldLabel>
              <Input id="li-tax" type="number" step="0.01" value={draft.tax_rate}
                onChange={(e) => setDraft({ ...draft, tax_rate: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="li-acct">Revenue account</FieldLabel>
            <SearchableSelect
              id="li-acct"
              value={draft.revenue_account ? String(draft.revenue_account) : NONE}
              onChange={(v) => setDraft({ ...draft, revenue_account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...revenueAccounts.map((a) => ({
                  value: String(a.id), label: a.name, hint: a.code,
                })),
              ]}
            />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// Strip computed line fields (subtotal/tax_amount/line_total) the backend
// rejects, re-stamp sort_order, and return a clean payload-ready array.
function serializeLines(lines: QuoteLine[]): QuoteLine[] {
  return lines.map((l, i) => ({
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
    tax_rate: l.tax_rate,
    revenue_account: l.revenue_account,
    product: l.product,
    sort_order: i,
  }))
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
